import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

// Feb-21: Floating install banner shown when the browser fires `beforeinstallprompt`.
// Hidden when already running as standalone (installed PWA) or dismissed this session.
export default function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // If already installed / running standalone, don't show
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true
      || document.referrer.startsWith("android-app://");
    if (isStandalone) return;
    if (sessionStorage.getItem("nb-install-dismissed")) { setDismissed(true); return; }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // Also clear when installed
    const onInstalled = () => setDeferred(null);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null);
  };
  const dismiss = () => { sessionStorage.setItem("nb-install-dismissed", "1"); setDismissed(true); };

  if (!deferred || dismissed) return null;
  return (
    <div data-testid="pwa-install-banner" className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-[9999] pointer-events-auto">
      <div className="rounded-2xl border border-nb-cyan/40 bg-[#0A0F19]/95 backdrop-blur-md p-4 shadow-2xl flex items-center gap-3">
        <div className="rounded-xl border border-nb-cyan/30 bg-nb-cyan/10 p-2.5 shrink-0">
          <Smartphone size={22} className="text-nb-cyan"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-white text-sm font-semibold">Install Next Billion App</div>
          <div className="text-[11px] text-slate-400 mt-0.5">Full-screen mobile experience · No Play Store needed.</div>
        </div>
        <button data-testid="pwa-install-yes" onClick={install} className="shrink-0 px-3 py-2 rounded-xl bg-nb-cyan text-black font-heading font-bold text-xs whitespace-nowrap hover:opacity-90">
          <Download size={12} className="inline mr-1"/>Install
        </button>
        <button data-testid="pwa-install-no" onClick={dismiss} className="shrink-0 p-1.5 text-slate-500 hover:text-white" aria-label="Dismiss">
          <X size={16}/>
        </button>
      </div>
    </div>
  );
}
